import React, { useState, useEffect } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import HotelDmcTabs from "./HotelDmcTabs";
import Header from "../../Common/Layout/Header";
import {
    RiSearchLine,
    RiCalendarLine,
    RiUserLine,
    RiHotelLine,
    RiMoneyDollarCircleLine,
    RiCheckLine,
    RiCloseLine,
    RiEyeLine,
    RiFilterLine,
    RiDownloadLine,
    RiMessageLine,
    RiWhatsappLine,
    RiMailLine,
    RiPhoneLine,
    RiArrowUpDownLine,
    RiCheckboxCircleLine,
    RiTimeLine,
    RiAlertLine
} from "react-icons/ri";

const BookingEnquiries = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    // Static booking enquiries data
    const [enquiries, setEnquiries] = useState([
        {
            id: 1,
            enquiryId: "ENQ-2024-001",
            agentName: "John Smith",
            agency: "Travel World Agency",
            email: "john@travelworld.com",
            phone: "+1-555-0123",
            whatsapp: "+1-555-0123",
            hotelName: "Grand Plaza Hotel",
            checkIn: "2024-03-15",
            checkOut: "2024-03-20",
            nights: 5,
            rooms: 2,
            adults: 4,
            children: 2,
            childrenAges: [5, 8],
            roomType: "Deluxe Sea View",
            mealPlan: "Breakfast Included",
            specialRequests: "Need early check-in at 12 PM",
            totalAmount: 1200,
            currency: "INR",
            status: "Pending",
            createdAt: "2024-02-25 10:30 AM",
            lastUpdated: "2024-02-25 10:30 AM"
        },
        {
            id: 2,
            enquiryId: "ENQ-2024-002",
            agentName: "Sarah Johnson",
            agency: "Luxury Travel Co.",
            email: "sarah@luxurytravel.com",
            phone: "+1-555-0456",
            whatsapp: "+1-555-0456",
            hotelName: "Ocean View Resort",
            checkIn: "2024-04-10",
            checkOut: "2024-04-15",
            nights: 5,
            rooms: 1,
            adults: 2,
            children: 0,
            childrenAges: [],
            roomType: "Premium Suite",
            mealPlan: "All Inclusive",
            specialRequests: "Honeymoon package with decoration",
            totalAmount: 850,
            currency: "INR",
            status: "Confirmed",
            createdAt: "2024-02-24 02:15 PM",
            lastUpdated: "2024-02-25 09:00 AM"
        },
        {
            id: 3,
            enquiryId: "ENQ-2024-003",
            agentName: "Mike Chen",
            agency: "Asia Pacific Tours",
            email: "mike@asiapacific.com",
            phone: "+65-9123-4567",
            whatsapp: "+65-9123-4567",
            hotelName: "Metropolitan Hotel",
            checkIn: "2024-05-01",
            checkOut: "2024-05-07",
            nights: 6,
            rooms: 3,
            adults: 6,
            children: 3,
            childrenAges: [3, 7, 10],
            roomType: "Executive Room",
            mealPlan: "Bed & Breakfast",
            specialRequests: "Interconnecting rooms required",
            totalAmount: 2200,
            currency: "INR",
            status: "Cancelled",
            createdAt: "2024-02-23 11:45 AM",
            lastUpdated: "2024-02-24 04:20 PM"
        },
        {
            id: 4,
            enquiryId: "ENQ-2024-004",
            agentName: "Emma Wilson",
            agency: "European Travel Network",
            email: "emma@eutravel.net",
            phone: "+44-7911-123456",
            whatsapp: "+44-7911-123456",
            hotelName: "Royal Palace Hotel",
            checkIn: "2024-06-12",
            checkOut: "2024-06-18",
            nights: 6,
            rooms: 4,
            adults: 8,
            children: 4,
            childrenAges: [2, 4, 6, 8],
            roomType: "Family Suite",
            mealPlan: "Half Board",
            specialRequests: "Airport transfer needed",
            totalAmount: 3500,
            currency: "INR",
            status: "Pending",
            createdAt: "2024-02-25 09:00 AM",
            lastUpdated: "2024-02-25 09:00 AM"
        },
        {
            id: 5,
            enquiryId: "ENQ-2024-005",
            agentName: "Alex Rodriguez",
            agency: "Americas Travel Group",
            email: "alex@americastravel.com",
            phone: "+1-555-0789",
            whatsapp: "+1-555-0789",
            hotelName: "Sunset Beach Resort",
            checkIn: "2024-07-20",
            checkOut: "2024-07-27",
            nights: 7,
            rooms: 2,
            adults: 4,
            children: 1,
            childrenAges: [9],
            roomType: "Beach Villa",
            mealPlan: "All Inclusive Plus",
            specialRequests: "Vegetarian meals required",
            totalAmount: 2800,
            currency: "INR",
            status: "Quoted",
            createdAt: "2024-02-24 04:30 PM",
            lastUpdated: "2024-02-25 11:15 AM"
        }
    ]);
    
    // Search and filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredEnquiries, setFilteredEnquiries] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");
    const [dateFilter, setDateFilter] = useState("All");
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    
    // Filter options
    const statusOptions = ["All", "Pending", "Confirmed", "Quoted", "Cancelled", "Completed"];
    const dateOptions = ["All", "Today", "Last 7 Days", "Last 30 Days", "This Month"];
    
    // Initialize filtered enquiries
    useEffect(() => {
        setFilteredEnquiries(enquiries);
    }, []);
    
    // Filter and search enquiries
    useEffect(() => {
        let filtered = [...enquiries];
        
        // Apply status filter
        if (statusFilter !== "All") {
            filtered = filtered.filter(enquiry => enquiry.status === statusFilter);
        }
        
        // Apply date filter
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (dateFilter === "Today") {
            filtered = filtered.filter(enquiry => {
                const enquiryDate = new Date(enquiry.createdAt.split(' ')[0]);
                return enquiryDate.getTime() === today.getTime();
            });
        } else if (dateFilter === "Last 7 Days") {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            filtered = filtered.filter(enquiry => {
                const enquiryDate = new Date(enquiry.createdAt.split(' ')[0]);
                return enquiryDate >= sevenDaysAgo;
            });
        } else if (dateFilter === "Last 30 Days") {
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            filtered = filtered.filter(enquiry => {
                const enquiryDate = new Date(enquiry.createdAt.split(' ')[0]);
                return enquiryDate >= thirtyDaysAgo;
            });
        } else if (dateFilter === "This Month") {
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            filtered = filtered.filter(enquiry => {
                const enquiryDate = new Date(enquiry.createdAt.split(' ')[0]);
                return enquiryDate >= firstDayOfMonth;
            });
        }
        
        // Apply search term
        if (searchTerm.trim() !== "") {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(enquiry => 
                enquiry.enquiryId.toLowerCase().includes(term) ||
                enquiry.agentName.toLowerCase().includes(term) ||
                enquiry.agency.toLowerCase().includes(term) ||
                enquiry.hotelName.toLowerCase().includes(term) ||
                enquiry.email.toLowerCase().includes(term)
            );
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortBy) {
                case "createdAt":
                    aValue = new Date(a.createdAt).getTime();
                    bValue = new Date(b.createdAt).getTime();
                    break;
                case "checkIn":
                    aValue = new Date(a.checkIn).getTime();
                    bValue = new Date(b.checkIn).getTime();
                    break;
                case "totalAmount":
                    aValue = a.totalAmount;
                    bValue = b.totalAmount;
                    break;
                case "agentName":
                    aValue = a.agentName.toLowerCase();
                    bValue = b.agentName.toLowerCase();
                    break;
                default:
                    aValue = new Date(a.createdAt).getTime();
                    bValue = new Date(b.createdAt).getTime();
            }
            
            return sortOrder === "desc" ? bValue - aValue : aValue - bValue;
        });
        
        setFilteredEnquiries(filtered);
    }, [searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);
    
    // Handle enquiry selection
    const handleViewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
    };
    
    // Handle status update
    const handleUpdateStatus = (enquiryId, newStatus) => {
        setEnquiries(prev => prev.map(enquiry => 
            enquiry.id === enquiryId 
                ? { ...enquiry, status: newStatus, lastUpdated: new Date().toLocaleString() }
                : enquiry
        ));
        
        // Also update selected enquiry if it's the same
        if (selectedEnquiry && selectedEnquiry.id === enquiryId) {
            setSelectedEnquiry(prev => ({
                ...prev,
                status: newStatus,
                lastUpdated: new Date().toLocaleString()
            }));
        }
    };
    
    // Handle contact actions
    const handleContactAgent = (enquiry, method) => {
        switch (method) {
            case 'whatsapp':
                window.open(`https://web.whatsapp.com/send?phone=${enquiry.whatsapp}`, '_blank');
                break;
            case 'email':
                window.open(`mailto:${enquiry.email}`, '_blank');
                break;
            case 'phone':
                window.open(`tel:${enquiry.phone}`, '_blank');
                break;
        }
    };
    
    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status) {
            case 'Confirmed':
                return 'success';
            case 'Pending':
                return 'warning';
            case 'Quoted':
                return 'info';
            case 'Cancelled':
                return 'danger';
            case 'Completed':
                return 'secondary';
            default:
                return 'light';
        }
    };
    
    // Toggle sort order
    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
    };
    
    // Format currency
    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };
    
    if (loading) return <p>Loading...</p>;
    
    if (!user) {
        navigate("/");
    }
    
    return (
        <div id="layout-wrapper">
             <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                <HotelDmcTabs pageName="BOOKINGENQUIRIES" />
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-12">
                                        {/* Header */}
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title text-primary mb-0">
                                                    <RiCalendarLine className="me-2" />
                                                    Booking Enquiries
                                                </h5>
                                                <small className="text-muted">
                                                    Manage and track all booking enquiries from agents
                                                </small>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className="btn btn-outline-primary"
                                                    onClick={() => window.print()}
                                                >
                                                    <RiDownloadLine className="me-1" />
                                                    Export
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Search and Filters Section */}
                                        <div className="card border-light mb-4">
                                            <div className="card-body">
                                                <div className="row g-3">
                                                    {/* Search */}
                                                    <div className="col-md-4">
                                                        <div className="input-group">
                                                            <span className="input-group-text">
                                                                <RiSearchLine />
                                                            </span>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Search by ID, Agent, Hotel, Email..."
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Status Filter */}
                                                    <div className="col-md-3">
                                                        <div className="input-group">
                                                            <span className="input-group-text">
                                                                <RiFilterLine />
                                                            </span>
                                                            <select
                                                                className="form-select"
                                                                value={statusFilter}
                                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                            >
                                                                {statusOptions.map(option => (
                                                                    <option key={option} value={option}>
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                   
                                                </div>
                                            </div>
                                        </div>
                                       
                                        
                                        {/* Main Content */}
                                        <div className="row">
                                            {/* Enquiries List */}
                                            <div className={`col-lg-${selectedEnquiry ? '7' : '12'}`}>
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6 className="mb-0">
                                                            Enquiries List
                                                            <span className="badge bg-primary ms-2">
                                                                {filteredEnquiries.length}
                                                            </span>
                                                        </h6>
                                                    </div>
                                                    <div className="card-body p-0">
                                                        {filteredEnquiries.length > 0 ? (
                                                            <div className="table-responsive">
                                                                <table className="table table-hover mb-0">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th style={{ width: '100px' }}>
                                                                                <div className="d-flex align-items-center cursor-pointer" onClick={() => toggleSort('createdAt')}>
                                                                                    Enquiry ID
                                                                                    {sortBy === 'createdAt' && (
                                                                                        <RiArrowUpDownLine className="ms-1" />
                                                                                    )}
                                                                                </div>
                                                                            </th>
                                                                            <th>
                                                                                <div className="d-flex align-items-center cursor-pointer" onClick={() => toggleSort('agentName')}>
                                                                                    Agent
                                                                                    {sortBy === 'agentName' && (
                                                                                        <RiArrowUpDownLine className="ms-1" />
                                                                                    )}
                                                                                </div>
                                                                            </th>
                                                                            <th>Hotel</th>
                                                                            <th>Check-in</th>
                                                                          
                                                                               <th>Check-Out</th>
                                                                           
                                                                            <th>Status</th>
                                                                            <th>Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {filteredEnquiries.map(enquiry => (
                                                                            <tr 
                                                                                key={enquiry.id}
                                                                                className={`cursor-pointer ${selectedEnquiry?.id === enquiry.id ? 'table-primary' : ''}`}
                                                                                onClick={() => handleViewEnquiry(enquiry)}
                                                                            >
                                                                                <td>
                                                                                    <strong className="text-primary">{enquiry.enquiryId}</strong>
                                                                                    <div className="small text-muted">
                                                                                        {enquiry.createdAt.split(' ')[0]}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="fw-medium">{enquiry.agentName}</div>
                                                                                    <div className="small text-muted">{enquiry.agency}</div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="fw-medium">{enquiry.hotelName}</div>
                                                                                    <div className="small text-muted">
                                                                                        {enquiry.rooms} room{enquiry.rooms > 1 ? 's' : ''}, {enquiry.nights} night{enquiry.nights > 1 ? 's' : ''}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div>{enquiry.checkIn}</div>
                                                                                    
                                                                                </td>
                                                                                <td>
                                                                                    <div>{enquiry.checkOut}</div>
                                                                                </td>
                                                                                <td>
                                                                                    <span className={`badge bg-${getStatusBadge(enquiry.status)}`}>
                                                                                        {enquiry.status}
                                                                                    </span>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="btn-group btn-group-sm">
                                                                                        <button
                                                                                            className="btn btn-outline-primary"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleViewEnquiry(enquiry);
                                                                                            }}
                                                                                            title="View Details"
                                                                                        >
                                                                                            <RiEyeLine />
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-5">
                                                                <RiSearchLine size={48} className="text-muted mb-3" />
                                                                <h5>No enquiries found</h5>
                                                                <p className="text-muted">Try adjusting your search criteria</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Enquiry Details Sidebar */}
                                            
                                            {/* Enquiry Details Sidebar */}
{selectedEnquiry && (
    <div className="col-lg-5">
        <div className="card" style={{ 
            position: 'sticky', 
            top: '80px', /* This prevents overlap with header */
            zIndex: 100,
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto'
        }}>
            <div className="card-header bg-primary bg-opacity-10">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-primary">
                        <RiEyeLine className="me-2" />
                        Enquiry Details
                    </h6>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setSelectedEnquiry(null)}
                    >
                        <RiCloseLine />
                        Close 
                    </button>
                </div>
            </div>
            <div className="card-body" style={{ paddingBottom: '20px' }}>
                {/* Enquiry Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h5 className="mb-1">{selectedEnquiry.hotelName}</h5>
                            <div className="d-flex align-items-center">
                                <span className={`badge bg-${getStatusBadge(selectedEnquiry.status)} me-2`}>
                                    {selectedEnquiry.status}
                                </span>
                                <small className="text-muted">
                                    ID: {selectedEnquiry.enquiryId}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Agent Details */}
                <div className="card border mb-3">
                    <div className="card-header bg-light py-2">
                        <h6 className="mb-0">
                            <RiUserLine className="me-2" />
                            Agent Details
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="mb-2">
                            <strong>Name:</strong>
                            <span className="ms-2">{selectedEnquiry.agentName}</span>
                        </div>
                        <div className="mb-2">
                            <strong>Agency:</strong>
                            <span className="ms-2">{selectedEnquiry.agency}</span>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleContactAgent(selectedEnquiry, 'whatsapp')}
                                title="Contact via WhatsApp"
                            >
                                <RiWhatsappLine className="me-1" />
                                WhatsApp
                            </button>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleContactAgent(selectedEnquiry, 'email')}
                                title="Send Email"
                            >
                                <RiMailLine className="me-1" />
                                Email
                            </button>
                            <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleContactAgent(selectedEnquiry, 'phone')}
                                title="Call"
                            >
                                <RiPhoneLine className="me-1" />
                                Call
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Booking Details */}
                <div className="card border mb-3">
                    <div className="card-header bg-light py-2">
                        <h6 className="mb-0">
                            <RiCalendarLine className="me-2" />
                            Booking Details
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Check-in</small>
                                <strong>{selectedEnquiry.checkIn}</strong>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Check-out</small>
                                <strong>{selectedEnquiry.checkOut}</strong>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Nights</small>
                                <strong>{selectedEnquiry.nights}</strong>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Rooms</small>
                                <strong>{selectedEnquiry.rooms}</strong>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Adults</small>
                                <strong>{selectedEnquiry.adults}</strong>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Children</small>
                                <strong>{selectedEnquiry.children}</strong>
                            </div>
                        </div>
                        <div className="mt-2">
                            <small className="text-muted d-block">Room Type</small>
                            <strong>{selectedEnquiry.roomType}</strong>
                        </div>
                        <div className="mt-2">
                            <small className="text-muted d-block">Meal Plan</small>
                            <strong>{selectedEnquiry.mealPlan}</strong>
                        </div>
                    </div>
                </div>
                
                {/* Special Requests */}
                {selectedEnquiry.specialRequests && (
                    <div className="card border mb-3">
                        <div className="card-header bg-light py-2">
                            <h6 className="mb-0">
                                <RiMessageLine className="me-2" />
                                Special Requests
                            </h6>
                        </div>
                        <div className="card-body">
                            <p className="mb-0">{selectedEnquiry.specialRequests}</p>
                        </div>
                    </div>
                )}
                
                {/* Timeline */}
                <div className="card border">
                    <div className="card-header bg-light py-2">
                        <h6 className="mb-0">
                            <RiTimeLine className="me-2" />
                            Timeline
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="mb-2">
                            <small className="text-muted d-block">Created</small>
                            <strong>{selectedEnquiry.createdAt}</strong>
                        </div>
                        <div className="mb-2">
                            <small className="text-muted d-block">Last Updated</small>
                            <strong>{selectedEnquiry.lastUpdated}</strong>
                        </div>
                    </div>
                </div>
                
                {/* Action Buttons */}
                <div className="mt-4">
                    <div className="d-flex justify-content-between">
                        <div className="btn-group">
                            <button
                                className="btn btn-success"
                                onClick={() => handleUpdateStatus(selectedEnquiry.id, 'Confirmed')}
                                disabled={selectedEnquiry.status === 'Confirmed'}
                            >
                                <RiCheckLine className="me-1" />
                                Confirm
                            </button>
                            <button
                                className="btn btn-info"
                                onClick={() => handleUpdateStatus(selectedEnquiry.id, 'Quoted')}
                                disabled={selectedEnquiry.status === 'Quoted'}
                            >
                                <RiMoneyDollarCircleLine className="me-1" />
                                Quote
                            </button>
                            <button
                                className="btn btn-warning"
                                onClick={() => handleUpdateStatus(selectedEnquiry.id, 'Pending')}
                                disabled={selectedEnquiry.status === 'Pending'}
                            >
                                <RiTimeLine className="me-1" />
                                Pending
                            </button>
                        </div>
                        <button
                            className="btn btn-danger"
                            onClick={() => handleUpdateStatus(selectedEnquiry.id, 'Cancelled')}
                        >
                            <RiCloseLine className="me-1" />
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Custom CSS */}
            <style jsx>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .sticky-top {
                    position: sticky;
                    z-index: 1020;
                }
                .nav-tabs-custom .nav-link {
                    border-bottom: 3px solid transparent;
                    padding: 0.75rem 1rem;
                    font-weight: 500;
                }
                .nav-tabs-custom .nav-link.active {
                    border-bottom-color: #0d6efd;
                    color: #0d6efd;
                }
                .nav-tabs-custom .nav-icon {
                    margin-right: 0.5rem;
                    font-size: 1.1rem;
                }
            `}</style>
        </div>
    );
};

export default BookingEnquiries;